import React from 'react';
import { AttorneyDashboard } from './legal/AttorneyDashboard';
import { ClientWaitingRoom } from './legal/ClientWaitingRoom';
import { VideoConsultation } from './legal/VideoConsultation';
import { AttorneyConsultation } from './legal/AttorneyConsultation';
import { ClientConsultation } from './legal/ClientConsultation';
import { RealtimeKitAttorneyConsultation } from './legal/RealtimeKitAttorneyConsultation';
import { SimpleAttorneyConsultation } from './legal/SimpleAttorneyConsultation';
import { RealtimeKitClientConsultation } from './legal/RealtimeKitClientConsultation';
import { JoinMeeting } from './legal/JoinMeeting';
import { CustomerTicketForm } from './support/CustomerTicketForm';
import { AgentDashboard } from './support/AgentDashboard';
import { SupportSession } from './support/SupportSession';
import { SupportVideoSession } from './support/SupportVideoSession';
import { CustomerWaitingRoom } from './support/CustomerWaitingRoom';
import { CustomerProjectForm } from './homeimprovement/CustomerProjectForm';
import { ContractorDashboard } from './homeimprovement/ContractorDashboard';
import { CustomerWaitingRoom as HomeImprovementWaitingRoom } from './homeimprovement/CustomerWaitingRoom';
import { HomeImprovementVideoSession } from './homeimprovement/HomeImprovementVideoSession';
import { BrandingCustomizer } from '../components/branding/BrandingCustomizer';
import { BrandingProvider } from '../contexts/BrandingContext';
import { UniversalWaitingRoom } from '../components/external/UniversalWaitingRoom';
import { ExternalSessionRouter } from '../components/external/ExternalSessionRouter';
import { UniversalAgentDashboard } from './universal/UniversalAgentDashboard';
import { UniversalVideoSession } from './universal/UniversalVideoSession';
import { UniversalWaitingRoom as UniversalWaitingRoomTemplate } from './universal/UniversalWaitingRoom';
import { UniversalSessionEnded } from './universal/UniversalSessionEnded';
import { UniversalRequestUnderReview } from './universal/UniversalRequestUnderReview';
import { GeneralRequestForm } from './general/GeneralRequestForm';
import { GeneralAgentDashboard } from './general/GeneralAgentDashboard';
import { GeneralAgentSession } from './general/GeneralAgentSession';
import { GeneralParticipantSession } from './general/GeneralParticipantSession';
import { UniversalRequestForm } from './universal/UniversalRequestForm';
import { useAuth } from '../contexts/AuthContext';
import { DemoLanding } from '../components/demo/DemoLanding';
import { DemoHost } from '../components/demo/DemoHost';
import { DemoGuest } from '../components/demo/DemoGuest';
import { DemoLimitReached } from '../components/demo/DemoLimitReached';
import { ProtectedDemoMonitor } from '../components/ProtectedDemoMonitor';
import { VideoConnectHost } from '../components/videoconnect/VideoConnectHost';
import { VideoConnectGuest } from '../components/videoconnect/VideoConnectGuest';

interface TemplateRouterProps {
  templatePath: string;
}

export const TemplateRouter: React.FC<TemplateRouterProps> = ({ templatePath }) => {
  // Remove /templates/ prefix if present, but preserve /public/ prefix
  let cleanPath = templatePath;
  if (templatePath.startsWith('/templates/')) {
    cleanPath = templatePath.replace(/^\/templates\//, '');
  } else if (templatePath.startsWith('/public/')) {
    cleanPath = templatePath.replace(/^\//, ''); // Remove leading slash only
  }
  
  // Parse the path: category/type/subtype
  const pathParts = cleanPath.split('/');
  const [category, type, subtype] = pathParts;

  switch (category) {
    case 'legal':
      return <LegalTemplateRouter type={type} subtype={subtype} />;

    case 'healthcare':
      return <HealthcareTemplateRouter type={type} subtype={subtype} />;

    case 'education':
      return <EducationTemplateRouter type={type} subtype={subtype} />;

    case 'interview':
      return <InterviewTemplateRouter type={type} subtype={subtype} />;

    case 'sales':
      return <SalesTemplateRouter type={type} subtype={subtype} />;

    case 'support':
      return <SupportTemplateRouter type={type} subtype={subtype} />;

    case 'homeimprovement':
      return <HomeImprovementTemplateRouter type={type} subtype={subtype} />;

    case 'external':
      return <ExternalTemplateRouter type={type} subtype={subtype} />;

    case 'universal':
      return <UniversalTemplateRouter type={type} subtype={subtype} />;

    case 'general':
      return <GeneralTemplateRouter type={type} subtype={subtype} />;
      
    case 'public':
      return <PublicTemplateRouter type={type} subtype={subtype} />;

    default:
      return <TemplateNotFound />;
  }
};

// Individual template routers
const LegalTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Extract org ID from URL path if present
  const extractOrgIdFromPath = () => {
    const pathname = window.location.pathname;
    const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
    return orgMatch ? orgMatch[1] : undefined;
  };
  
  const orgIdFromUrl = extractOrgIdFromPath();
  
  switch (type) {
    case 'preview':
      return <LegalTemplatePreview />;
    case 'attorney':
      return <AttorneyDashboard firmName="Demo Law Firm" attorneyName="Attorney Smith" />;
    case 'demo':
      return <ClientWaitingRoom firmName="Smith & Associates Law Firm" organizationId={orgIdFromUrl} />;
    case 'client':
      return <ClientWaitingRoom firmName="Smith & Associates Law Firm" organizationId={orgIdFromUrl} />;
    case 'join':
      return <JoinMeeting />;
    case 'branding':
      return (
        <BrandingProvider templateType="legal">
          <BrandingCustomizer />
        </BrandingProvider>
      );
    case 'session':
      // New RealtimeKit-based session routes
      if (subtype === 'host') {
        return (
          <SimpleAttorneyConsultation
            roomId={urlParams.get('roomId') || undefined}
            attorneyName={urlParams.get('attorneyName') || 'Attorney Smith'}
            firmName={urlParams.get('firmName') || 'Smith & Associates Law Firm'}
            requestId={urlParams.get('requestId') || undefined}
            onEndConsultation={() => {
              window.history.pushState({}, '', '/templates/legal/attorney');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        );
      } else if (subtype === 'participant') {
        // Use working RealtimeKitClientConsultation for legal client sessions
        return (
          <RealtimeKitClientConsultation
            roomId={urlParams.get('roomId') || undefined}
            clientName={urlParams.get('clientName') || urlParams.get('participantName') || 'John Doe'}
            requestId={urlParams.get('requestId') || undefined}
          />
        );
      }
      return <TemplateNotFound />;
    case 'consultation':
          // Handle both old single consultation route and new separate routes
          if (subtype === 'attorney') {
            // /templates/legal/consultation/attorney
            const urlParams = new URLSearchParams(window.location.search);
            return (
              <AttorneyConsultation
                attorneyName={urlParams.get('attorneyName') || 'Attorney Smith'}
                firmName={urlParams.get('firmName') || 'Smith & Associates Law Firm'}
                requestId={urlParams.get('requestId') || 'CONS-2024-001'}
              />
            );
          } else if (subtype === 'client') {
            // /templates/legal/consultation/client
            const urlParams = new URLSearchParams(window.location.search);
            return (
              <ClientConsultation
                clientName={urlParams.get('clientName') || 'John Doe'}
                requestId={urlParams.get('requestId') || 'CONS-2024-001'}
              />
            );
          } else {
            // Fallback to original consultation route for backward compatibility
            const urlParams = new URLSearchParams(window.location.search);
            const userType = urlParams.get('type');
            
            if (userType === 'legal_attorney') {
              return (
                <AttorneyConsultation
                  attorneyName="Attorney Smith"
                  firmName="Smith & Associates Law Firm"
                  requestId={urlParams.get('requestId') || 'CONS-2024-001'}
                />
              );
            } else if (userType === 'legal_client') {
              return (
                <ClientConsultation
                  clientName={urlParams.get('clientName') || 'John Doe'}
                  requestId={urlParams.get('requestId') || 'CONS-2024-001'}
                />
              );
            } else {
              // Default to original VideoConsultation for backward compatibility
              return (
                <VideoConsultation
                  clientName="John Doe"
                  caseType="Personal Injury"
                  attorneyName="Attorney Smith"
                  firmName="Smith & Associates Law Firm"
                  consultationId="CONS-2024-001"
                  onEndConsultation={() => {
                    window.history.pushState({}, '', '/templates/legal/attorney');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  onSaveNotes={(notes: any) => {
                    console.log('Saving notes:', notes);
                  }}
                  onScheduleFollowUp={() => {
                    console.log('Scheduling follow-up');
                  }}
                />
              );
            }
          }
          break;
    default:
      return <LegalTemplatePreview />;
  }
};

const HealthcareTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <HealthcareTemplatePreview />;
    case 'session':
      if (subtype === 'host') {
        // Doctor interface - coming soon
        return <ComingSoonTemplate category="Healthcare" name="Doctor Interface" />;
      } else if (subtype === 'participant') {
        // Patient interface - coming soon
        return <ComingSoonTemplate category="Healthcare" name="Patient Interface" />;
      }
      return <TemplateNotFound />;
    default:
      return <ComingSoonTemplate category="Healthcare" name="Telemedicine Platform" />;
  }
};

const EducationTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <EducationTemplatePreview />;
    case 'session':
      if (subtype === 'host') {
        // Teacher interface - coming soon
        return <ComingSoonTemplate category="Education" name="Teacher Interface" />;
      } else if (subtype === 'participant') {
        // Student interface - coming soon
        return <ComingSoonTemplate category="Education" name="Student Interface" />;
      }
      return <TemplateNotFound />;
    default:
      return <ComingSoonTemplate category="Education" name="Virtual Classroom" />;
  }
};

const InterviewTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <InterviewTemplatePreview />;
    case 'session':
      if (subtype === 'host') {
        // Interviewer interface - coming soon
        return <ComingSoonTemplate category="HR" name="Interviewer Interface" />;
      } else if (subtype === 'participant') {
        // Candidate interface - coming soon
        return <ComingSoonTemplate category="HR" name="Candidate Interface" />;
      }
      return <TemplateNotFound />;
    default:
      return <ComingSoonTemplate category="HR" name="Interview Platform" />;
  }
};

const SalesTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <SalesTemplatePreview />;
    case 'session':
      if (subtype === 'host') {
        // Sales rep interface - coming soon
        return <ComingSoonTemplate category="Sales" name="Sales Rep Interface" />;
      } else if (subtype === 'participant') {
        // Prospect interface - coming soon
        return <ComingSoonTemplate category="Sales" name="Prospect Interface" />;
      }
      return <TemplateNotFound />;
    default:
      return <ComingSoonTemplate category="Sales" name="Sales Demo Platform" />;
  }
};

const SupportTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <SupportTemplatePreview />;
    case 'agent':
      return <AgentDashboard 
        agentName={urlParams.get('agentName') || 'Support Agent'}
        companyName={urlParams.get('companyName') || 'Support Center'}
      />;
    case 'customer':
      return <CustomerTicketForm 
        companyName={urlParams.get('companyName') || 'Support Center'}
      />;
    case 'ticket':
      return <CustomerTicketForm 
        companyName={urlParams.get('companyName') || 'Support Center'}
      />;
    case 'waiting':
      // Get ticket ID from the path: /templates/support/waiting/{ticketId}
      const ticketId = subtype; // The subtype will be the ticket ID
      return <CustomerWaitingRoom 
        companyName={urlParams.get('companyName') || 'Support Center'}
        ticketId={ticketId}
      />;
    case 'branding':
      return (
        <BrandingProvider templateType="business">
          <BrandingCustomizer />
        </BrandingProvider>
      );
    case 'session':
      if (subtype === 'agent') {
        const roomId = urlParams.get('roomId') || '';
        const agentName = urlParams.get('agentName') || 'Support Agent';
        const ticketId = urlParams.get('ticketId') || '';
        
        // Debug logging
        console.log('🎥 [SupportTemplateRouter] Agent session params:', {
          roomId,
          agentName,
          ticketId,
          allParams: Object.fromEntries(urlParams.entries())
        });
        
        return <SupportVideoSession
          roomId={roomId}
          ticketId={ticketId}
          participantType="agent"
          participantName={agentName}
          ticketDetails={{
            subject: urlParams.get('subject') || 'Support Request',
            category: urlParams.get('category') || 'general',
            priority: urlParams.get('priority') || 'medium',
            customerName: urlParams.get('customerName') || 'Customer'
          }}
        />;
      } else if (subtype === 'customer') {
        return <SupportVideoSession
          roomId={urlParams.get('roomId') || ''}
          ticketId={urlParams.get('ticketId') || ''}
          participantType="customer"
          participantName={urlParams.get('customerName') || 'Customer'}
          ticketDetails={{
            subject: urlParams.get('subject') || 'Support Request',
            category: urlParams.get('category') || 'general',
            priority: urlParams.get('priority') || 'medium',
            agentName: urlParams.get('agentName') || 'Support Agent'
          }}
        />;
      }
      return <TemplateNotFound />;
    default:
      return <SupportTemplatePreview />;
  }
};

const HomeImprovementTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'preview':
      return <HomeImprovementTemplatePreview />;
    case 'contractor':
      return <ContractorDashboard 
        contractorName={urlParams.get('contractorName') || 'Contractor'}
        companyName={urlParams.get('companyName') || 'HomeExpert Contractors'}
        contractorId={urlParams.get('contractorId') || 'contractor_001'}
      />;
    case 'customer':
      return <CustomerProjectForm 
        companyName={urlParams.get('companyName') || 'HomeExpert Contractors'}
      />;
    case 'project':
      return <CustomerProjectForm 
        companyName={urlParams.get('companyName') || 'HomeExpert Contractors'}
      />;
    case 'waiting':
      // Get project ID from the path: /templates/homeimprovement/waiting/{projectId}
      const projectId = subtype; // The subtype will be the project ID
      return <HomeImprovementWaitingRoom 
        companyName={urlParams.get('companyName') || 'HomeExpert Contractors'}
        projectId={projectId}
      />;
    case 'branding':
      return (
        <BrandingProvider templateType="business">
          <BrandingCustomizer />
        </BrandingProvider>
      );
    case 'session':
      if (subtype === 'contractor') {
        return <HomeImprovementVideoSession
          roomId={urlParams.get('roomId') || ''}
          projectId={urlParams.get('projectId') || ''}
          participantType="contractor"
          participantName={urlParams.get('contractorName') || 'Contractor'}
          projectDetails={{
            title: urlParams.get('projectTitle') || 'Home Improvement Project',
            category: urlParams.get('category') || 'general',
            urgency: urlParams.get('urgency') || 'planning',
            customerName: urlParams.get('customerName') || 'Customer'
          }}
        />;
      } else if (subtype === 'customer') {
        return <HomeImprovementVideoSession
          roomId={urlParams.get('roomId') || ''}
          projectId={urlParams.get('projectId') || ''}
          participantType="customer"
          participantName={urlParams.get('customerName') || 'Customer'}
          projectDetails={{
            title: urlParams.get('projectTitle') || 'Home Improvement Project',
            category: urlParams.get('category') || 'general',
            urgency: urlParams.get('urgency') || 'planning',
            contractorName: urlParams.get('contractorName') || 'Contractor'
          }}
        />;
      }
      return <TemplateNotFound />;
    default:
      return <HomeImprovementTemplatePreview />;
  }
};

// External API template router
const ExternalTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  switch (type) {
    case 'session':
      // Handle external session routing: /external/session/{sessionId}
      const sessionId = subtype;
      if (sessionId) {
        return <ExternalSessionRouter sessionId={sessionId} />;
      }
      return <TemplateNotFound />;
    case 'waiting':
      // Handle external waiting room: /external/waiting/{sessionId}
      const waitingSessionId = subtype;
      if (waitingSessionId) {
        return <UniversalWaitingRoom sessionId={waitingSessionId} />;
      }
      return <TemplateNotFound />;
    default:
      return <TemplateNotFound />;
  }
};

// Universal Agent Dashboard router
const UniversalTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const { currentWorkspace, account } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  
  console.log('[UniversalTemplateRouter] Routing to type:', type, 'subtype:', subtype);
  
  // Public routes that don't require authentication
  const publicRoutes = ['preview', 'waiting', 'demo', 'session-ended', 'request-under-review', 'request-form', 'session'];
  
  // Check auth only for protected routes
  if (!publicRoutes.includes(type) && (!currentWorkspace || !account)) {
    return <div className="p-8 text-center">
      <p className="text-red-600">Authentication required. Please sign in to access the universal dashboard.</p>
    </div>;
  }
  
  switch (type) {
    case 'preview':
      return <UniversalTemplatePreview />;
    case 'dashboard':
      return <UniversalAgentDashboard 
        agentName={`${account?.firstName} ${account?.lastName}`.trim() || 'Universal Agent'}
        agentId={account?.id || ''}
        organizationId={currentWorkspace?.organizationId || ''}
      />;
    case 'session':
      // Support both authenticated hosts and public customers
      const participantType = urlParams.get('participantType') || 'host';
      const isCustomer = participantType === 'customer';
      
      // For customers, use URL params only. For hosts, prefer auth context with URL fallback
      const agentName = isCustomer 
        ? urlParams.get('agentName') || 'Universal Agent'
        : (account ? `${account.firstName} ${account.lastName}`.trim() : '') || urlParams.get('agentName') || 'Universal Agent';
      
      return <UniversalVideoSession
        roomId={urlParams.get('roomId') || ''}
        requestId={urlParams.get('requestId') || ''}
        agentName={agentName}
        participantType={participantType as 'host' | 'customer'}
        customerName={urlParams.get('customerName') || 'Customer'}
        sessionDetails={{
          title: urlParams.get('title') || 'Universal Consultation',
          category: urlParams.get('category') || 'consultation',
          urgency: urlParams.get('urgency') || 'medium'
        }}
      />;
    case 'waiting':
      // Universal waiting room for external sessions
      const requestId = subtype;
      if (requestId) {
        return <UniversalWaitingRoomTemplate 
          requestId={requestId}
          sessionId={urlParams.get('sessionId') || undefined}
        />;
      }
      return <TemplateNotFound />;
    case 'session-ended':
      // Universal session ended page - mobile optimized
      return <UniversalSessionEnded 
        customerName={urlParams.get('customerName') || undefined}
        agentName={urlParams.get('agentName') || undefined}
        sessionDuration={urlParams.get('sessionDuration') || undefined}
        companyName={urlParams.get('companyName') || undefined}
      />;
    case 'request-under-review':
      // Universal request under review page - customer-friendly rejection handling
      return <UniversalRequestUnderReview 
        customerName={urlParams.get('customerName') || undefined}
        companyName={urlParams.get('companyName') || undefined}
        sessionId={urlParams.get('sessionId') || undefined}
        originalCategory={urlParams.get('category') || undefined}
      />;
    case 'request-form':
      // Universal request form - public form for new/resubmitted requests
      return <UniversalRequestForm 
        organizationId={currentWorkspace?.organizationId || ''}
        companyName={urlParams.get('companyName') || undefined}
      />;
    default:
      return <TemplateNotFound />;
  }
};

// General Template Router
const GeneralTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Extract org ID from URL path if present
  const extractOrgIdFromPath = () => {
    const pathname = window.location.pathname;
    const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
    return orgMatch ? orgMatch[1] : undefined;
  };
  
  const orgIdFromUrl = extractOrgIdFromPath();
  
  switch (type) {
    case 'preview':
      return <GeneralRequestForm companyName="Customer Connect" organizationId={orgIdFromUrl} />;
    case 'demo':
      return <GeneralRequestForm companyName="Customer Connect" organizationId={orgIdFromUrl} />;
    case 'dashboard':
      return <GeneralAgentDashboard agentName="Agent" companyName="Customer Connect" />;
    case 'branding':
      return (
        <BrandingProvider templateType="general">
          <BrandingCustomizer />
        </BrandingProvider>
      );
    case 'session':
      if (subtype === 'host') {
        return (
          <GeneralAgentSession
            roomId={urlParams.get('roomId') || undefined}
            agentName={urlParams.get('agentName') || 'Agent'}
            companyName={urlParams.get('companyName') || 'Customer Connect'}
            requestId={urlParams.get('requestId') || undefined}
            onEndSession={() => {
              window.history.pushState({}, '', '/templates/general/dashboard');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        );
      } else if (subtype === 'participant') {
        return (
          <GeneralParticipantSession
            roomId={urlParams.get('roomId') || undefined}
            clientName={urlParams.get('clientName') || urlParams.get('participantName') || 'Customer'}
            requestId={urlParams.get('requestId') || undefined}
          />
        );
      }
      return <TemplateNotFound />;
    default:
      return <TemplateNotFound />;
  }
};

// Public Template Router (Viral Demo)
const PublicTemplateRouter: React.FC<{ type: string; subtype: string }> = ({ type, subtype }) => {
  const urlParams = new URLSearchParams(window.location.search);

  switch (type) {
    case 'demo':
      if (!subtype) {
        // Landing page: /public/demo
        return <DemoLanding />;
      } else if (subtype === 'host') {
        // Host interface with session ID from URL path: /public/demo/host/{sessionId}
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        return <DemoHost
          sessionId={sessionId}
          roomId={urlParams.get('roomId') || undefined}
          hostName={urlParams.get('hostName') || undefined}
        />;
      } else if (subtype === 'guest') {
        // Guest interface with session ID from URL path: /public/demo/guest/{sessionId}
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        return <DemoGuest
          sessionId={sessionId}
          roomId={urlParams.get('roomId') || undefined}
          guestName={urlParams.get('guestName') || undefined}
        />;
      } else if (subtype === 'limit-reached') {
        // Conversion page: /public/demo/limit-reached
        return <DemoLimitReached />;
      } else if (subtype === 'monitor') {
        // Public monitoring dashboard: /public/demo/monitor
        return <ProtectedDemoMonitor />;
      }
      return <TemplateNotFound />;
    case 'video-connect':
      if (subtype === 'host') {
        // Host interface with session ID from URL path: /public/video-connect/host/{sessionId}
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        return <VideoConnectHost
          sessionId={sessionId}
          roomId={urlParams.get('roomId') || undefined}
          hostName={urlParams.get('hostName') || undefined}
        />;
      } else if (subtype === 'guest') {
        // Guest interface with session ID from URL path: /public/video-connect/guest/{sessionId}
        const pathParts = window.location.pathname.split('/');
        const sessionId = pathParts[pathParts.length - 1];
        return <VideoConnectGuest
          sessionId={sessionId}
          roomId={urlParams.get('roomId') || undefined}
          guestName={urlParams.get('guestName') || undefined}
        />;
      }
      return <TemplateNotFound />;
    default:
      return <TemplateNotFound />;
  }
};

// Legal template overview/preview page
const LegalTemplatePreview: React.FC = () => {
  return (
    <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Legal Call Center Template</h1>
          <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
            Complete video consultation system designed specifically for law firms. 
            Professional, secure, and compliant with legal industry standards.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Attorney View Preview */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
              <h3 className="text-xl font-semibold mb-2">Attorney Dashboard</h3>
              <p className="text-blue-100">Manage incoming consultations and client queue</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time client queue</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Case type and urgency indicators</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Billable time tracking</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Case notes and documentation</span>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => {
                    window.history.pushState({}, '', '/templates/legal/attorney');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Preview Attorney Dashboard
                </button>
              </div>
            </div>
          </div>

          {/* Client View Preview */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <h3 className="text-xl font-semibold mb-2">Client Experience</h3>
              <p className="text-green-100">Professional intake and waiting room</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Comprehensive intake forms</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Device testing and setup</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Professional waiting room</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Document upload capability</span>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => {
                    window.history.pushState({}, '', '/templates/legal/client');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  className="block w-full bg-green-600 text-white text-center py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Preview Client Experience
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Branding Customization */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Template Customization</h3>
            <p className="text-purple-100">Brand this template with your company's identity</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Custom colors and branding</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Company logo and information</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Typography and layout options</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Persistent across all sessions</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/legal/branding');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-purple-600 text-white text-center py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                🎨 Customize Legal Template Branding
              </button>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg p-8 mb-8 border border-light-border dark:border-dark-border">
          <h3 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mb-6">Template Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Legal Compliance</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Attorney-client privilege notices and legal disclaimers</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Secure & Private</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">End-to-end encryption and secure document handling</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1V8z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Practice Management</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Billing integration and case management tools</p>
            </div>
          </div>
        </div>

        {/* Deployment Instructions */}
        <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-8 text-white border border-gray-700 dark:border-gray-600">
          <h3 className="text-2xl font-bold mb-6">Quick Deployment</h3>
          <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700 dark:border-gray-600">
            <code className="text-green-400 dark:text-green-300">
              {`<iframe 
  src="https://customerconnects.app/templates/legal?token=YOUR_API_KEY"
  width="100%" 
  height="600"
/>`}
            </code>
          </div>
          <p className="text-gray-300 dark:text-gray-400">
            Deploy this template on your law firm's website with a single iframe embed. 
            Customize branding, intake forms, and workflows to match your practice.
          </p>
        </div>
      </div>
    </div>
  );
};

// Healthcare template preview
const HealthcareTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Healthcare Telemedicine Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          HIPAA-compliant telemedicine platform for remote patient consultations and medical care.
        </p>
      </div>
      <div className="text-center">
        <p className="text-lg text-blue-600 dark:text-blue-400 mb-4">🏥 Coming Soon</p>
        <p className="text-light-text-secondary dark:text-dark-text-secondary">Doctor/Patient video consultation system with medical records integration.</p>
      </div>
    </div>
  </div>
);

// Education template preview
const EducationTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Education Virtual Classroom Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Interactive virtual classroom with whiteboard, breakout rooms, and student engagement tools.
        </p>
      </div>
      <div className="text-center">
        <p className="text-lg text-blue-600 dark:text-blue-400 mb-4">🎓 Coming Soon</p>
        <p className="text-light-text-secondary dark:text-dark-text-secondary">Teacher/Student virtual classroom with interactive learning tools.</p>
      </div>
    </div>
  </div>
);

// Interview template preview
const InterviewTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">HR Interview Platform Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Professional interview platform with candidate evaluation tools and ATS integration.
        </p>
      </div>
      <div className="text-center">
        <p className="text-lg text-blue-600 dark:text-blue-400 mb-4">💼 Coming Soon</p>
        <p className="text-light-text-secondary dark:text-dark-text-secondary">Interviewer/Candidate platform with evaluation and scheduling tools.</p>
      </div>
    </div>
  </div>
);

// Sales template preview
const SalesTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Sales Demo Platform Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Interactive sales demo platform with CRM integration and lead management tools.
        </p>
      </div>
      <div className="text-center">
        <p className="text-lg text-blue-600 dark:text-blue-400 mb-4">💰 Coming Soon</p>
        <p className="text-light-text-secondary dark:text-dark-text-secondary">Sales Rep/Prospect demo platform with CRM and analytics integration.</p>
      </div>
    </div>
  </div>
);

// Support template preview
const SupportTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Customer Support Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Complete customer support platform with ticket management, live video support, and knowledge base integration.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Agent Dashboard Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Agent Dashboard</h3>
            <p className="text-blue-100">Manage support tickets and customer queue</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time ticket queue</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Smart ticket routing by category</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Customer history and interactions</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Performance analytics</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/support/agent');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Preview Agent Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Customer Interface Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Customer Interface</h3>
            <p className="text-green-100">Easy ticket submission and support experience</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Simple ticket submission form</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">File and screenshot uploads</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Priority level selection</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time status updates</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/support/customer');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-green-600 text-white text-center py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Preview Customer Form
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Live Support Features */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Live Support Session</h3>
          <p className="text-purple-100">Video calls with screen sharing and real-time chat</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Video Calls</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">HD video and audio support</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Screen Sharing</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Share screens for troubleshooting</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Live Chat</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">In-session messaging and file sharing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Customization */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Template Customization</h3>
          <p className="text-orange-100">Brand this template with your company's identity</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Custom colors and branding</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Company logo and information</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Typography and layout options</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Persistent across all sessions</span>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => {
                window.history.pushState({}, '', '/templates/support/branding');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="block w-full bg-orange-600 text-white text-center py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              🎨 Customize Support Template Branding
            </button>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg p-8 mb-8 border border-light-border dark:border-dark-border">
        <h3 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mb-6">Template Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Smart Routing</h4>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Automatic ticket assignment based on category and expertise</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Knowledge Base</h4>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Integrated help articles and solution suggestions</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1V8z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Analytics</h4>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Performance metrics and customer satisfaction tracking</p>
          </div>
        </div>
      </div>

      {/* Deployment Instructions */}
      <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-8 text-white border border-gray-700 dark:border-gray-600">
        <h3 className="text-2xl font-bold mb-6">Quick Deployment</h3>
        <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700 dark:border-gray-600">
          <code className="text-green-400 dark:text-green-300">
            {`<iframe 
  src="https://customerconnects.app/templates/support?token=YOUR_API_KEY"
  width="100%" 
  height="600"
/>`}
          </code>
        </div>
        <p className="text-gray-300 dark:text-gray-400">
          Deploy this template on your website with a single iframe embed. 
          Customize branding, ticket routing, and support workflows to match your business.
        </p>
      </div>
    </div>
  </div>
);

// Home Improvement template preview page
const HomeImprovementTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Home Improvement Call Center Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Complete home improvement consultation platform with mobile-optimized customer forms, contractor dashboards, and video estimates.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Customer Interface Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Mobile Customer Form</h3>
            <p className="text-orange-100">Optimized project submission for homeowners</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Mobile-first responsive design</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Photo upload for project areas</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Project category selection</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Budget range and urgency levels</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Location-based contractor matching</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/homeimprovement/customer');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-orange-600 text-white text-center py-3 rounded-lg hover:bg-orange-700 transition-colors"
              >
                Preview Customer Form
              </button>
            </div>
          </div>
        </div>

        {/* Contractor Dashboard Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Contractor Dashboard</h3>
            <p className="text-blue-100">Professional project management interface</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time project queue</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Project photos and details</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Distance and value calculations</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Customer contact information</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">One-click video consultations</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/homeimprovement/contractor');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Preview Contractor Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Consultation Features */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Video Consultation Features</h3>
          <p className="text-green-100">Professional remote estimates and consultations</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">HD Video Calls</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Crystal clear video for detailed project discussions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Screen Sharing</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Share designs, plans, and reference materials</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 00-1 1v.01L4 5z" clipRule="evenodd" />
                  <path d="M5.5 4.5a.5.5 0 11-1 0 .5.5 0 011 0zM12.5 4.5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Live Estimates</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Create and share estimates during the call</p>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Section */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg p-8 mb-8 border border-light-border dark:border-dark-border">
        <h3 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mb-6">Customization & Branding</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">Brand Your Experience</h4>
            <ul className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
              <li>• Custom company logo and colors</li>
              <li>• Personalized messaging and taglines</li>
              <li>• White-label customer experience</li>
              <li>• Custom domain integration</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">Contractor Specialties</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-2 py-1 rounded">🍳 Kitchen</span>
              <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">🛁 Bathroom</span>
              <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded">🏠 Roofing</span>
              <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">🎨 Painting</span>
              <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded">⚡ Electrical</span>
              <span className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded">🔧 Plumbing</span>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={() => {
              window.history.pushState({}, '', '/templates/homeimprovement/branding');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="block w-full bg-orange-600 text-white text-center py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            🎨 Customize Home Improvement Template
          </button>
        </div>
      </div>

      {/* Deployment Instructions */}
      <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-8 text-white border border-gray-700 dark:border-gray-600">
        <h3 className="text-2xl font-bold mb-6">Quick Deployment</h3>
        <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700 dark:border-gray-600">
          <code className="text-green-400 dark:text-green-300">
            {`<iframe 
  src="https://customerconnects.app/templates/homeimprovement?token=YOUR_API_KEY"
  width="100%" 
  height="600"
/>`}
          </code>
        </div>
        <p className="text-gray-300 dark:text-gray-400">
          Deploy this template for your contracting business with a single iframe embed. 
          Perfect for home improvement companies, contractors, and renovation services.
        </p>
      </div>
    </div>
  </div>
);

// Universal Agent Dashboard template preview page
const UniversalTemplatePreview: React.FC = () => (
  <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Universal Agent Dashboard Template</h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Centralized multi-template dashboard for agents to handle requests from Legal, Support, and Home Improvement templates through a unified interface with external API integration.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Universal Dashboard Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Unified Request Management</h3>
            <p className="text-blue-100">Handle all template requests from one central dashboard</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Cross-template request queue</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Template-specific request filtering</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Priority and urgency sorting</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time request notifications</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">One-click session acceptance</span>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/templates/universal/dashboard');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Preview Universal Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* External API Integration Preview */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden border border-light-border dark:border-dark-border">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">External API Integration</h3>
            <p className="text-purple-100">Zapier, webhooks, and third-party service integration</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Zapier workflow integration</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Webhook endpoint support</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">External session URL generation</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Multi-tenant API key management</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Real-time status tracking</span>
              </div>
            </div>
            <div className="mt-6">
              <button className="block w-full bg-purple-600 text-white text-center py-3 rounded-lg hover:bg-purple-700 transition-colors">
                View API Documentation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Template Support Matrix */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Multi-Template Support</h3>
          <p className="text-green-100">Unified interface for all Customer Connects templates</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">⚖️ Legal Services</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Handle legal consultations, case intake, and attorney sessions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">🎧 Customer Support</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Manage support tickets, technical issues, and customer queries</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2v8h12V6H4z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">🏠 Home Improvement</h4>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Process contractor requests, project estimates, and consultations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics & Reporting */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Consolidated Analytics</h3>
          <p className="text-indigo-100">Cross-template metrics and performance insights</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">📊</div>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">Total Requests</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">⏱️</div>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">Avg Wait Time</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">📈</div>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">Today's Handled</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">✅</div>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">Active Sessions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Customization */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg overflow-hidden mb-8 border border-light-border dark:border-dark-border">
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">Universal Branding</h3>
          <p className="text-pink-100">Consistent branding across all template interactions</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-pink-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Unified brand identity across templates</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-pink-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Custom agent dashboard themes</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-pink-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">White-label external API responses</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-pink-500 rounded-full mr-3"></div>
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Consistent user experience</span>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => {
                window.history.pushState({}, '', '/templates/universal/branding');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="block w-full bg-pink-600 text-white text-center py-3 rounded-lg hover:bg-pink-700 transition-colors"
            >
              🎨 Customize Universal Template Branding
            </button>
          </div>
        </div>
      </div>

      {/* API Integration Setup */}
      <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-8 text-white border border-gray-700 dark:border-gray-600">
        <h3 className="text-2xl font-bold mb-6">API Integration Setup</h3>
        
        {/* Request Example */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-blue-300">Sample Request</h4>
          <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4 border border-gray-700 dark:border-gray-600 overflow-x-auto">
            <code className="text-green-400 dark:text-green-300 text-sm whitespace-pre">
              {`curl -X POST https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/external/generate-session-url \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_type": "universal",
    "participant_data": {
      "customer_name": "UI Test Customer",
      "customer_email": "uitest@example.com",
      "agent_name": "Universal Agent"
    },
    "project_details": {
      "title": "UI Separation Test Session",
      "category": "technical-support",
      "urgency": "high",
      "description": "Testing separate UI for host vs customer in universal template"
    },
    "organization_config": {
      "company_name": "Test Company"
    },
    "metadata": {
      "source": "UI Test",
      "environment": "development"
    }
  }'`}
            </code>
          </div>
        </div>

        {/* Response Example */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-purple-300">Sample Response</h4>
          <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4 border border-gray-700 dark:border-gray-600 overflow-x-auto">
            <code className="text-yellow-400 dark:text-yellow-300 text-sm whitespace-pre">
              {`{
  "success": true,
  "session_id": "ext_sess_1754493939108_8id1n2",
  "request_id": "universal_ext_1754493939108",
  "template_type": "universal",
  "expires_at": "2025-08-07T15:25:39.108Z",
  "estimated_wait_time": "5-10 minutes",
  "urls": {
    "customer": {
      "waiting_room": "https://app.customerconnect.live/templates/universal/waiting/universal_ext_1754493939108?sessionId=ext_sess_1754493939108_8id1n2&external=true",
      "session_url": "https://app.customerconnect.live/external/ext_sess_1754493939108_8id1n2"
    },
    "host": {
      "dashboard": "https://app.customerconnect.live/templates/universal/dashboard",
      "direct_accept": "https://app.customerconnect.live/templates/universal/dashboard?highlight=ext_sess_1754493939108_8id1n2"
    }
  },
  "session_url": "https://app.customerconnect.live/external/ext_sess_1754493939108_8id1n2"
}`}
            </code>
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="space-y-4">
          <p className="text-gray-300 dark:text-gray-400">
            <strong className="text-white">Integration Options:</strong>
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 dark:text-gray-400 ml-4">
            <li><strong className="text-blue-300">Zapier:</strong> Use this endpoint in Zapier workflows to create sessions automatically</li>
            <li><strong className="text-green-300">Webhooks:</strong> Send session data from your forms, CRM, or other systems</li>
            <li><strong className="text-purple-300">Custom Apps:</strong> Integrate directly into your applications and services</li>
          </ul>
          <p className="text-gray-300 dark:text-gray-400 mt-4">
            Send customers to the <code className="bg-gray-800 px-2 py-1 rounded text-yellow-300">customer.waiting_room</code> URL, 
            and agents will see the request in their <code className="bg-gray-800 px-2 py-1 rounded text-blue-300">host.dashboard</code>.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Coming soon template placeholder
const ComingSoonTemplate: React.FC<{ category: string; name: string }> = ({ category, name }) => {
  return (
    <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-full flex items-center justify-center mx-auto mb-6 border border-light-border dark:border-dark-border">
          <svg className="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">{name}</h1>
        <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary mb-8">
          This {category.toLowerCase()} template is coming soon. We're working hard to bring you 
          industry-specific video calling solutions.
        </p>
        <div className="space-y-4">
          <button className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors">
            Notify Me When Available
          </button>
          <a 
            href="/templates" 
            className="block w-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-primary dark:text-dark-text-primary py-3 px-6 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors border border-light-border dark:border-dark-border"
          >
            Browse Available Templates
          </a>
        </div>
      </div>
    </div>
  );
};

// 404 for templates
const TemplateNotFound: React.FC = () => {
  return (
    <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">Template Not Found</h1>
        <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary mb-8">
          The template you're looking for doesn't exist or may have been moved.
        </p>
        <a 
          href="/templates" 
          className="inline-block bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Browse All Templates
        </a>
      </div>
    </div>
  );
};